import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import DraggableModal from "../DraggableModal";
import EmailDialog from "../EmailDialog";
import { htmlToPdfBlob, safeFilename } from "../../pdfRenderer";
import { escHtml, reportStyles, policyHeader, policyFooter, openPreviewWindow, downloadBlob } from "./auditCommon";
import { loadFromSupabase } from "../../supabaseDB";
import { appendDisclosureByAccount } from "../emailHelpers";

const T = {
  en: {
    title: "Traceability Procedure",
    close: "Close", preview: "Preview", pdf: "Download PDF", email: "Email", busy: "Working…", sending: "Sending…",
    secObjective: "Objective", secPolicy: "Policy", secScope: "Scope", secMaterials: "Materials", secProcedure: "Procedure Development",
    secDef: "Definition", secForward: "Forward Traceability (to client)", secBackward: "Backward Traceability (from supplier)",
    defText: "Traceability is the system that allows the history, use and location of the product to be recovered through registered codes, in order to identify its origin and location along the supply chain and enable an efficient recall when needed.",
    fwd1: "When pallets are assembled, a pallet number is generated and recorded in the Pallet Conformation format, identifying traceability codes, number of cases, ticket number and product temperatures.",
    fwd2: "A shipment manifest is prepared identifying the pallet numbers that make up the load, destination data and the corresponding invoice number.",
    fwd3: "Transport inspection is performed and the load is released if it complies with requirements.",
    bwd1: "The producer delivers product packed and coded with labels on each case and clamshell; the supplier traceability code consists of 8 digits as shown below.",
    bwd2: "At fruit receipt, the producer is given a ticket recording the code, time, number of cases and quality description.",
    version: "Version",
    secFlow: "Flow Diagram with Hazard Analysis (F: Physical · B: Biological · Q: Chemical)",
    st1: "Fruit Receipt", st2: "Fruit Inspection", st3: "Palletizing", st4: "Forced-Air Pre-Cooling", st5: "Cold Storage", st6: "Transport",
    h1: "F: foreign material, needles, labels, stationery, jewelry · B: personnel hands (E.Coli, CT, CF, Salmonella) · Q: field application residues",
    h2: "F: foreign material, needles, labels, stationery, jewelry · B: personnel hands (E.Coli, CT, CF, Salmonella) · Q: field application residues",
    h3: "F: foreign material, jewelry, staples, cutter · B: personnel hands, temperature · Q: N/A",
    h4: "F: N/A · B: plastic curtain (E.Coli, CT, CF, Salmonella, Listeria), temperature · Q: N/A",
    h5: "F: jewelry · B: Listeria, CT, CF, Salmonella, E.Coli, temperature · Q: refrigerants, 6% chlorine",
    h6: "F: jewelry · B: personnel hands (Listeria, E.Coli, CT, CF, Salmonella), temperature · Q: 6% chlorine",
    objective: "Define the labeling system for packaging (various) in order to identify the finished product, across collection points, distribution lines and the final consumer.",
    policy: "Ground-Fresh International Inc. implements this traceability system applying it to all of its product suppliers, in order to trace and recover product whenever needed for any food-safety and/or quality reason.",
    scope: "This procedure applies to the producers — suppliers of product packed in packaging (various) for Ground-Fresh International Inc.",
    materials: ["Labeling machine", "Rolls of adhesive labels", "Ink"],
    procedure: "On the labeling machine provided by the company, the following information must be integrated:",
    fProducer: "Producer Code: 3 digits", fRanch: "Ranch/Sector Number: 2 digits", fJulian: "Julian Day: 3 digits",
    lblProducer: "Producer Code", lblRanch: "Ranch Number", lblJulian: "Julian Day",
    loading: "Loading company data…",
    emailSubject: "Traceability Procedure",
    emailBody: "Attached is the Traceability Procedure for audit ",
  },
  es: {
    title: "Procedimiento de Trazabilidad",
    close: "Cerrar", preview: "Vista previa", pdf: "Descargar PDF", email: "Correo", busy: "Trabajando…", sending: "Enviando…",
    secObjective: "Objetivo", secPolicy: "Política", secScope: "Alcance", secMaterials: "Materiales", secProcedure: "Desarrollo del procedimiento",
    secDef: "Definición", secForward: "Trazabilidad hacia adelante (al cliente)", secBackward: "Trazabilidad hacia atrás (del proveedor)",
    defText: "La trazabilidad es el sistema por el cual se puede recuperar la historia, utilización y localización del producto por medio de códigos registrados, con el fin de identificar su origen y ubicación a lo largo de la cadena de abastecimiento y permitir un retiro eficiente en caso necesario.",
    fwd1: "Al conformar pallets se genera un número de pallet que se registra en el formato Conformación de Pallet, identificando códigos de trazabilidad, número de cajas, número de ticket y temperaturas del producto.",
    fwd2: "Se elabora un manifiesto de embarque identificando los números de pallet que conforman la carga, datos del destino y el número de factura correspondiente.",
    fwd3: "Se realiza la inspección del transporte y se libera la carga si cumple con lo solicitado.",
    bwd1: "El productor entrega el producto empacado y codificado con etiquetas en cada caja y clamshell; el código de trazabilidad del proveedor consta de 8 dígitos como se muestra a continuación.",
    bwd2: "A la recepción de fruta se entrega al productor un ticket donde se registra el código, hora, número de cajas y descripción de calidad.",
    version: "Versión",
    secFlow: "Diagrama de Flujo con Análisis de Peligros (F: Físico · B: Biológico · Q: Químico)",
    st1: "Recepción de Fruta", st2: "Revisión de Fruta", st3: "Palletizado", st4: "Pre-Frío de Extracción Forzada", st5: "Almacenamiento", st6: "Transporte",
    h1: "F: materiales extraños, termómetros, etiquetas, papelería, joyería · B: manos del personal (E.Coli, CT, CF, Salmonella) · Q: residuos de aplicación desde campo",
    h2: "F: materiales extraños, termómetros, etiquetas, papelería, joyería · B: manos del personal (E.Coli, CT, CF, Salmonella) · Q: residuos de aplicación desde campo",
    h3: "F: materiales extraños, joyería, grapas, cutter · B: manos del personal, temperatura · Q: N/A",
    h4: "F: N/A · B: cortina de plástico (E.Coli, CT, CF, Salmonella, Listeria), temperatura · Q: N/A",
    h5: "F: joyería · B: Listeria, CT, CF, Salmonella, E.Coli, temperatura · Q: refrigerantes, cloro 6%",
    h6: "F: joyería · B: manos del personal (Listeria, E.Coli, CT, CF, Salmonella), temperatura · Q: cloro 6%",
    objective: "Definir el sistema de etiquetado para embalajes (varios) con la finalidad de identificar el producto terminado, tanto en acopios, líneas de distribución y consumidor final.",
    policy: "Ground-Fresh Internacional Inc. tiene a bien implementar este sistema de trazabilidad aplicándolo a todos sus proveedores de producto, para poder realizar rastreo y recuperación de producto cuando se tenga necesidad por cualquier motivo de inocuidad y/o calidad.",
    scope: "Este procedimiento es aplicable a los productores – proveedores de producto empacado en embalajes (varios) Ground-Fresh Internacional Inc.",
    materials: ["Etiquetadora", "Rollos de etiquetas adheribles", "Tinta"],
    procedure: "En etiquetadora proporcionada por la empresa, deberá integrarse la siguiente información:",
    fProducer: "Código del Productor: 3 dígitos", fRanch: "Número de rancho/Sector: 2 dígitos", fJulian: "Día Juliano: 3 dígitos",
    lblProducer: "Código Productor", lblRanch: "Número de rancho", lblJulian: "Día Juliano",
    loading: "Cargando datos de la empresa…",
    emailSubject: "Procedimiento de Trazabilidad",
    emailBody: "Adjunto el Procedimiento de Trazabilidad para la auditoría ",
  },
};

function hazardBox(x, y, w, h, fill, stroke, num, title, hazards) {
  var t = '<g><rect x="'+x+'" y="'+y+'" width="'+w+'" height="'+h+'" rx="8" fill="'+fill+'" stroke="'+stroke+'" stroke-width="1.2"/>';
  t += '<circle cx="'+(x+16)+'" cy="'+(y+16)+'" r="10" fill="'+stroke+'"/>';
  t += '<text x="'+(x+16)+'" y="'+(y+20)+'" text-anchor="middle" font-size="11" font-weight="700" fill="#fff">'+num+'</text>';
  t += '<text x="'+(x+34)+'" y="'+(y+20)+'" font-size="12" font-weight="700" fill="'+stroke+'">'+escHtml(title)+'</text>';
  var words = String(hazards).split(" ");
  var line = ""; var lines = []; var maxChars = 46;
  for (var i=0;i<words.length;i++){ if ((line+" "+words[i]).length>maxChars){ lines.push(line); line=words[i]; } else { line = line ? line+" "+words[i] : words[i]; } }
  if (line) lines.push(line);
  for (var j=0;j<lines.length && j<4;j++){ t += '<text x="'+(x+12)+'" y="'+(y+38+j*12)+'" font-size="8.5" fill="#444">'+escHtml(lines[j])+'</text>'; }
  return t + '</g>';
}

function hazardFlowSvg(t) {
  var GREEN_F="#E1F5EE", GREEN_S="#0F6E56", BLUE_F="#E6F1FB", BLUE_S="#185FA5", AMBER_F="#FAEEDA", AMBER_S="#854F0B";
  function arrowV(x,y1,y2){ return '<line x1="'+x+'" y1="'+y1+'" x2="'+x+'" y2="'+y2+'" stroke="#888" stroke-width="1.5" marker-end="url(#arh)"/>'; }
  function arrowH(x1,y,x2){ return '<line x1="'+x1+'" y1="'+y+'" x2="'+x2+'" y2="'+y+'" stroke="#888" stroke-width="1.5" marker-end="url(#arh)"/>'; }
  var s = '<svg width="100%" viewBox="0 0 620 470" xmlns="http://www.w3.org/2000/svg" style="font-family:Arial,Helvetica,sans-serif">';
  s += '<defs><marker id="arh" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M2 1L8 5L2 9" fill="none" stroke="#888" stroke-width="1.5"/></marker></defs>';
  s += hazardBox(30, 20, 250, 78, GREEN_F, GREEN_S, "1", t.st1, t.h1);
  s += hazardBox(340, 20, 250, 78, GREEN_F, GREEN_S, "2", t.st2, t.h2);
  s += hazardBox(340, 130, 250, 78, BLUE_F, BLUE_S, "3", t.st3, t.h3);
  s += hazardBox(30, 130, 250, 78, BLUE_F, BLUE_S, "4", t.st4, t.h4);
  s += hazardBox(30, 240, 250, 78, AMBER_F, AMBER_S, "5", t.st5, t.h5);
  s += hazardBox(340, 240, 250, 78, AMBER_F, AMBER_S, "6", t.st6, t.h6);
  s += arrowH(280, 59, 338);
  s += arrowV(465, 98, 130);
  s += arrowH(340, 169, 282);
  s += arrowV(155, 208, 240);
  s += arrowH(280, 279, 338);
  s += '</svg>';
  return s;
}

function labelDiagramSvg(t) {
  return ''
    + '<svg width="100%" viewBox="0 0 620 300" xmlns="http://www.w3.org/2000/svg" style="font-family:Arial,Helvetica,sans-serif">'
    + '<defs><marker id="ar2" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M2 1L8 5L2 9" fill="none" stroke="#555" stroke-width="1.5"/></marker></defs>'
    + '<rect x="200" y="110" width="220" height="80" rx="6" fill="#F8F5EC" stroke="#5F5E5A" stroke-width="1.5"/>'
    + '<text x="248" y="162" text-anchor="middle" font-size="34" font-weight="700" fill="#1a1a1a" font-family="\'Courier New\',monospace">023</text>'
    + '<text x="312" y="162" text-anchor="middle" font-size="34" font-weight="700" fill="#1a1a1a" font-family="\'Courier New\',monospace">02</text>'
    + '<text x="372" y="162" text-anchor="middle" font-size="34" font-weight="700" fill="#1a1a1a" font-family="\'Courier New\',monospace">041</text>'
    + '<line x1="286" y1="115" x2="286" y2="185" stroke="#999" stroke-width="1"/>'
    + '<line x1="338" y1="115" x2="338" y2="185" stroke="#999" stroke-width="1"/>'
    + '<text x="120" y="240" text-anchor="middle" font-size="12" font-weight="700" fill="#0F6E56">'+escHtml(t.lblProducer)+'</text>'
    + '<line x1="120" y1="225" x2="240" y2="175" stroke="#555" stroke-width="1.2" marker-end="url(#ar2)"/>'
    + '<text x="312" y="70" text-anchor="middle" font-size="12" font-weight="700" fill="#185FA5">'+escHtml(t.lblRanch)+'</text>'
    + '<line x1="312" y1="78" x2="312" y2="108" stroke="#555" stroke-width="1.2" marker-end="url(#ar2)"/>'
    + '<text x="500" y="240" text-anchor="middle" font-size="12" font-weight="700" fill="#854F0B">'+escHtml(t.lblJulian)+'</text>'
    + '<line x1="500" y1="225" x2="385" y2="175" stroke="#555" stroke-width="1.2" marker-end="url(#ar2)"/>'
    + '</svg>';
}

function buildHtml(data, audit, lang) {
  var t = T[lang === "es" ? "es" : "en"];
  var co = data.company || {}; var cert = data.cert || {};
  var certNo = co.organicCert || cert.certNumber || "";
  var verStr = audit && audit.year ? (t.version + " " + audit.year + "-" + (Number(audit.year) + 1)) : "";
  var matRows = t.materials.map(function(m){ return '<li>' + escHtml(m) + '</li>'; }).join("");
  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><style>' + reportStyles()
    + '.page{max-width:1040px}'
    + '@page{size:letter landscape;margin:0.45in 0.5in}'
    + '.flow-wrap svg{max-width:560px;width:100%;height:auto}'
    + '.diagram-wrap svg{max-width:520px;width:100%;height:auto}'
    + '.flow-wrap, .diagram-wrap{text-align:center;padding:6px}'
    + '.section{margin-top:10px}'
    + '.tr-body{font-size:11.5px;line-height:1.55;color:#1a1a1a;margin-top:4px;max-width:920px;word-spacing:1px}'
    + '.tr-ul{word-spacing:1px}'
    + '.tr-ul{margin:4px 0 4px 20px;font-size:11.5px}'
    + '.tr-fields{margin:4px 0 4px 20px;font-size:11.5px;color:#333}'
    + '.diagram-wrap{margin-top:10px;border:1px solid #d1d5db;border-radius:6px;padding:10px;text-align:center}'
    + '</style></head><body><div class="page">';
  h += policyHeader(t.title, (certNo ? "Organic certificate #: " + certNo : ""), verStr);
  h += '<div class="section"><div class="section-title">' + t.secObjective + '</div><div class="tr-body">' + escHtml(t.objective) + '</div></div>';
  h += '<div class="section"><div class="section-title">' + t.secPolicy + '</div><div class="tr-body">' + escHtml(t.policy) + '</div></div>';
  h += '<div class="section"><div class="section-title">' + t.secScope + '</div><div class="tr-body">' + escHtml(t.scope) + '</div></div>';
  h += '<div class="section"><div class="section-title">' + t.secDef + '</div><div class="tr-body">' + escHtml(t.defText) + '</div></div>';
  h += '<div class="section"><div class="section-title">' + t.secFlow + '</div><div class="diagram-wrap">' + hazardFlowSvg(t) + '</div></div>';
  h += '<div class="section"><div class="section-title">' + t.secForward + '</div><ul class="tr-ul"><li>' + escHtml(t.fwd1) + '</li><li>' + escHtml(t.fwd2) + '</li><li>' + escHtml(t.fwd3) + '</li></ul></div>';
  h += '<div class="section"><div class="section-title">' + t.secBackward + '</div><ul class="tr-ul"><li>' + escHtml(t.bwd1) + '</li><li>' + escHtml(t.bwd2) + '</li></ul></div>';
  h += '<div class="section"><div class="section-title">' + t.secMaterials + '</div><ul class="tr-ul">' + matRows + '</ul></div>';
  h += '<div class="section"><div class="section-title">' + t.secProcedure + '</div><div class="tr-body">' + escHtml(t.procedure) + '</div>';
  h += '<div class="tr-fields">' + escHtml(t.fProducer) + '<br/>' + escHtml(t.fRanch) + '<br/>' + escHtml(t.fJulian) + '</div>';
  h += '<div class="diagram-wrap">' + labelDiagramSvg(t) + '</div></div>';
  h += policyFooter();
  return h + '</div></body></html>';
}

export default function TraceabilityProcedurePanel({ lang, audit, onClose, onLogReport, initialParams }) {
  const t = T[lang === "es" ? "es" : "en"];
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const lastBlobRef = useRef(null); const lastFilenameRef = useRef("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const profiles = await loadFromSupabase("company_profile");
        const company = (profiles || []).find(p => String(p.id) === "gfi") || (profiles || [])[0] || {};
        const certs = await loadFromSupabase("certifications");
        const cert = (certs || []).find(c => c && c.entityType === "gfi" && c.certType === "organic") || {};
        if (alive) setData({ company, cert });
      } catch (e) {
        console.error("[Traceability] load failed", e);
        if (alive) setData({ company: {}, cert: {} });
      }
    })();
    return () => { alive = false; };
  }, []);

  const makeHtml = useCallback(() => buildHtml(data || { company:{}, cert:{} }, audit, lang), [data, audit, lang]);
  const previewHtml = useMemo(() => makeHtml(), [makeHtml]);
  const currentParams = useMemo(() => ({}), []);

  const handlePreview = () => openPreviewWindow(makeHtml());

  const handlePdf = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await htmlToPdfBlob(makeHtml(), { orientation: "landscape", widthPx: 1056 });
      const fn = safeFilename("Traceability_" + (audit && audit.name || "")) + ".pdf";
      lastBlobRef.current = blob; lastFilenameRef.current = fn;
      downloadBlob(blob, fn);
      onLogReport({ type: "traceability_proc", title: (lang==="es"?"PDF descargado":"PDF downloaded") + " · " + fn, params: currentParams, blob: blob, filename: fn, mime: "application/pdf" });
    } catch (e) {
      console.error("[Traceability] pdf failed", e);
      window.alert((lang==="es"?"Error generando PDF: ":"PDF generation failed: ") + (e.message || String(e)));
    } finally { setBusy(false); }
  };

  const company = (data && data.company) || {};
  /* 2026-09-30 — excludes Sweetleaf/automated accounts, same fix as
     OrganicAuditRegistry.js's own emailAccounts. */
  const emailAccounts = ((company && company.emailAccounts) || []).filter(function (a) { var n = (a.name || "").toLowerCase(); return n.indexOf("sweetleaf") < 0 && n.indexOf("automat") < 0; });
  const fromEmail = (company && (company.outboundEmail || company.email)) || "";
  const signatures = (company && company.emailSignatures) || [];

  const btnPrimary = { padding: "8px 16px", borderRadius: 6, border: "none", background: "#1B3A4B", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13 };
  const btnSecondary = { padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text)", cursor: "pointer", fontSize: 13 };

  return (
    <DraggableModal title={t.title + " · " + (audit && audit.name || "")} onClose={onClose} zIndex={10003}>
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
        {!data ? (
          <div style={{ color: "var(--color-text-muted)", fontStyle: "italic", padding: 20 }}>{t.loading}</div>
        ) : (
          <div style={{ border: "1px solid var(--color-border)", borderRadius: 6, overflow: "hidden", background: "#fff", height: 460 }}>
            <iframe title="traceability-preview" srcDoc={previewHtml} style={{ width: "100%", height: "100%", border: 0 }} />
          </div>
        )}
      </div>
      <div style={{ padding: "12px 22px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{busy ? t.sending : ""}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={btnSecondary}>{t.close}</button>
          <button onClick={handlePreview} style={btnSecondary}>{t.preview}</button>
          <button onClick={() => setShowEmail(true)} style={btnSecondary}>{t.email}</button>
          <button onClick={handlePdf} style={btnPrimary} disabled={busy}>{busy ? t.busy : t.pdf}</button>
        </div>
      </div>

      {showEmail && (
        <EmailDialog
          lang={lang}
          title={t.email}
          recipients={(audit && audit.auditorEmail) ? [{ name: audit.auditorName || "", email: audit.auditorEmail, checked: true }] : []}
          docOptions={[{ key: "report", label: t.title, checked: true }]}
          defaultSubject={t.emailSubject + " — " + (audit && audit.name || "")}
          defaultBody={t.emailBody + (audit && audit.name || "") + "."}
          fromEmail={fromEmail}
          emailAccounts={emailAccounts}
          signatures={signatures}
          postProcessHtmlBody={(html, resolvedFrom) => appendDisclosureByAccount(html, resolvedFrom, company, null)}
          buildAttachments={async () => {
            const blob = await htmlToPdfBlob(makeHtml(), { orientation: "landscape", widthPx: 1056 });
            const fn = safeFilename("Traceability_" + (audit && audit.name || "")) + ".pdf";
            lastBlobRef.current = blob; lastFilenameRef.current = fn;
            return [{ filename: fn, blob: blob, mimeType: "application/pdf" }];
          }}
          onSent={() => {
            const to = (audit && audit.auditorEmail) ? [audit.auditorEmail] : [];
            onLogReport({ type: "traceability_proc", title: lang === "es" ? "Enviado por correo" : "Sent by email", recipients: to, params: currentParams, blob: lastBlobRef.current, filename: lastFilenameRef.current, mime: "application/pdf" });
            setShowEmail(false);
          }}
          onClose={() => setShowEmail(false)}
        />
      )}
    </DraggableModal>
  );
}
